import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import NewsCard from "@/components/base/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import { movies } from "@/mocks/movies";
import { news } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const typeOptions = ["全部", "资讯", "影片"] as const;
type TypeOption = (typeof typeOptions)[number];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [type, setType] = useState<TypeOption>("全部");

  useSeo({
    title: query ? `搜索“${query}” - 91电影` : "全站搜索 - 91电影",
    description: "在91电影中搜索电影资讯、影评与影片推荐，快速找到你想了解的影片信息。",
    keywords: "网站搜索,影片搜索,资讯搜索,91电影",
  });

  const keyword = query.trim().toLowerCase();

  const matchedNews = useMemo(() => {
    if (!keyword) return [];
    return news.filter(
      (n) =>
        n.title.toLowerCase().includes(keyword) ||
        n.excerpt.toLowerCase().includes(keyword) ||
        n.tags.some((t) => t.toLowerCase().includes(keyword)),
    );
  }, [keyword]);

  const matchedMovies = useMemo(() => {
    if (!keyword) return [];
    return movies.filter(
      (m) =>
        m.title.toLowerCase().includes(keyword) ||
        m.genres.some((g) => g.toLowerCase().includes(keyword)) ||
        m.director.toLowerCase().includes(keyword) ||
        m.cast.some((c) => c.toLowerCase().includes(keyword)),
    );
  }, [keyword]);

  const total = matchedNews.length + matchedMovies.length;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setParams(q ? { q } : {});
  };

  const hotMovies = movies.filter((m) => m.hot).slice(0, 4);
  const hotNews = news.slice(0, 3);

  return (
    <div className="w-full px-4 md:px-8 py-8 animate-fade-up">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="mt-5 max-w-3xl">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">全站搜索</h1>
        <p className="text-sm md:text-base text-foreground-500 mt-2">
          输入关键词，搜索电影资讯与影片推荐。
        </p>
      </div>

      <form onSubmit={submit} className="mt-6 max-w-3xl">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 bg-background-50 border border-background-200 rounded-md px-4 h-12 flex-1 focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-500 text-lg" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="搜索影片名称、类型、导演或资讯关键词"
              className="bg-transparent outline-none text-sm w-full text-foreground-800 placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
          >
            搜索
          </button>
        </div>
      </form>

      {query && (
        <div className="mt-7 max-w-3xl">
          <p className="text-sm text-foreground-500">
            当前搜索词：
            <span className="font-bold text-primary-700 mx-1">{query}</span>
            · 共找到 <span className="font-bold text-accent-600">{total}</span> 条结果
          </p>
        </div>
      )}

      {query && total > 0 && (
        <div className="flex flex-wrap gap-2 mt-5">
          {typeOptions.map((opt) => {
            const count =
              opt === "全部" ? total : opt === "资讯" ? matchedNews.length : matchedMovies.length;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setType(opt)}
                className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  type === opt
                    ? "bg-primary-500 text-background-50 font-medium"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {opt}（{count}）
              </button>
            );
          })}
        </div>
      )}

      {!query ? (
        <EmptyState
          icon="ri-search-eye-line"
          title="输入关键词开始搜索"
          description="试试搜索“科幻”“星海彼岸”或“票房”等关键词。"
        />
      ) : total === 0 ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-emotion-sad-line"
            title={`没有找到与“${query}”相关的内容`}
            description="换个关键词试试，或者从下面的热门内容开始浏览。"
          />
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-1.5 h-6 rounded-full bg-accent-500" />
              <h2 className="font-heading font-bold text-lg text-foreground-950">热门推荐</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {hotMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
              {hotNews.map((n) => (
                <NewsCard key={n.id} item={n} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8 flex flex-col gap-12">
          {(type === "全部" || type === "资讯") && matchedNews.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-primary-500" />
                <h2 className="font-heading font-bold text-lg text-foreground-950">
                  资讯结果（{matchedNews.length}）
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {matchedNews.map((n) => (
                  <NewsCard key={n.id} item={n} />
                ))}
              </div>
            </section>
          )}

          {(type === "全部" || type === "影片") && matchedMovies.length > 0 && (
            <section>
              <div className="flex items-center gap-3 mb-5">
                <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                <h2 className="font-heading font-bold text-lg text-foreground-950">
                  影片结果（{matchedMovies.length}）
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {matchedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <div className="mt-14 bg-secondary-100 rounded-lg border border-background-200 p-6 md:p-8">
        <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4">热门搜索词</h2>
        <div className="flex flex-wrap gap-2">
          {["星海彼岸", "科幻", "悬疑", "票房", "影评", "动作片", "治愈", "高分电影"].map((tag) => (
            <Link
              key={tag}
              to={`/search?q=${encodeURIComponent(tag)}`}
              className="px-3.5 py-1.5 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {tag}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}